<template>
  <div class="space-y-6">
    <!-- Card de Configuração do Cora -->
    <div class="card">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-xl font-semibold" style="color: var(--text-primary)">🏦 {{ t('financeiro.configuracoes.coraIntegration') }}</h3>
          <p class="text-sm mt-1" style="color: var(--text-secondary)">{{ t('financeiro.configuracoes.coraSubtitle') }}</p>
        </div>
        <div v-if="statusCora.configurado" class="flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800">
          <span class="w-2 h-2 rounded-full bg-green-600"></span>
          <span class="text-sm font-medium">{{ t('financeiro.configuracoes.configured') }}</span>
        </div>
        <div v-else class="flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-800">
          <span class="w-2 h-2 rounded-full bg-orange-600"></span>
          <span class="text-sm font-medium">{{ t('financeiro.configuracoes.notConfigured') }}</span>
        </div>
      </div>

      <form @submit.prevent="salvarCredenciais" class="space-y-4">
        <!-- Client ID -->
        <div>
          <label class="block text-sm font-medium mb-1" style="color: var(--text-secondary)">
            {{ t('financeiro.configuracoes.clientId') }} *
          </label>
          <input 
            v-model="form.clientId" 
            type="text" 
            required 
            class="input-field w-full"
            :placeholder="t('financeiro.configuracoes.clientIdPlaceholder')"
          />
        </div>

        <!-- Client Secret -->
        <div>
          <label class="block text-sm font-medium mb-1" style="color: var(--text-secondary)">
            {{ t('financeiro.configuracoes.clientSecretOptional') }}
          </label>
          <input 
            v-model="form.clientSecret" 
            :type="mostrarSecret ? 'text' : 'password'" 
            class="input-field w-full"
            :placeholder="t('financeiro.configuracoes.clientSecretPlaceholder')"
          />
          <button 
            type="button" 
            @click="mostrarSecret = !mostrarSecret"
            class="text-xs mt-1 text-blue-600 hover:underline"
          >
            {{ mostrarSecret ? '👁️ ' + t('financeiro.configuracoes.hide') : '👁️‍🗨️ ' + t('financeiro.configuracoes.show') }}
          </button>
          <p class="text-xs mt-1" style="color: var(--text-secondary)">
            ℹ️ {{ t('financeiro.configuracoes.clientSecretHint') }}
          </p>
        </div>

        <!-- Certificado -->
        <div>
          <label class="block text-sm font-medium mb-2" style="color: var(--text-secondary)">
            📜 {{ t('financeiro.configuracoes.certificate') }}
          </label>
          <div class="border-2 border-dashed rounded-lg p-4" style="border-color: var(--border)">
            <input 
              type="file" 
              @change="handleCertificateUpload"
              accept=".pem,.crt,.cer"
              class="hidden"
              ref="certificateInput"
            />
            <button 
              type="button"
              @click="$refs.certificateInput.click()"
              class="w-full px-4 py-3 rounded-lg border-2 transition-all hover:bg-opacity-10 hover:bg-blue-500"
              style="border-color: var(--border)"
            >
              <span v-if="!form.certificateFileName" style="color: var(--text-secondary)">
                📁 {{ t('financeiro.configuracoes.selectCertificate') }}
              </span>
              <span v-else class="flex items-center justify-between">
                <span style="color: var(--text-primary)">✅ {{ form.certificateFileName }}</span>
                <button @click.stop="limparCertificado" class="text-red-600 hover:text-red-700 ml-2">✖</button>
              </span>
            </button>
            <p class="text-xs mt-2" style="color: var(--text-secondary)">
              {{ t('financeiro.configuracoes.certificateHint') }}
            </p>
          </div>
        </div>

        <!-- Private Key -->
        <div>
          <label class="block text-sm font-medium mb-2" style="color: var(--text-secondary)">
            🔑 {{ t('financeiro.configuracoes.privateKey') }}
          </label>
          <div class="border-2 border-dashed rounded-lg p-4" style="border-color: var(--border)">
            <input 
              type="file" 
              @change="handlePrivateKeyUpload"
              accept=".pem,.key"
              class="hidden"
              ref="privateKeyInput"
            />
            <button 
              type="button"
              @click="$refs.privateKeyInput.click()"
              class="w-full px-4 py-3 rounded-lg border-2 transition-all hover:bg-opacity-10 hover:bg-blue-500"
              style="border-color: var(--border)"
            >
              <span v-if="!form.privateKeyFileName" style="color: var(--text-secondary)">
                📁 {{ t('financeiro.configuracoes.selectPrivateKey') }}
              </span>
              <span v-else class="flex items-center justify-between">
                <span style="color: var(--text-primary)">✅ {{ form.privateKeyFileName }}</span>
                <button @click.stop="limparPrivateKey" class="text-red-600 hover:text-red-700 ml-2">✖</button>
              </span>
            </button>
            <p class="text-xs mt-2" style="color: var(--text-secondary)">
              {{ t('financeiro.configuracoes.privateKeyHint') }}
            </p>
          </div>
        </div>

        <!-- Ambiente -->
        <div>
          <label class="block text-sm font-medium mb-1" style="color: var(--text-secondary)">
            {{ t('financeiro.configuracoes.environment') }} *
          </label>
          <select v-model="form.ambiente" required class="input-field w-full">
            <option value="HML">🧪 {{ t('financeiro.configuracoes.envHml') }}</option>
            <option value="PROD">🚀 {{ t('financeiro.configuracoes.envProd') }}</option>
          </select>
          <p class="text-xs mt-1" style="color: var(--text-secondary)">
            <span v-if="form.ambiente === 'HML'">
              ⚠️ {{ t('financeiro.configuracoes.envHmlWarning') }}
            </span>
            <span v-else class="text-orange-600">
              ⚡ {{ t('financeiro.configuracoes.envProdWarning') }}
            </span>
          </p>
        </div>

        <!-- Info Box -->
        <div class="p-4 rounded-lg" style="background-color: var(--bg-primary); border: 1px solid var(--border)">
          <p class="text-sm font-medium mb-2" style="color: var(--text-primary)">ℹ️ {{ t('financeiro.configuracoes.integrationTypes') }}</p>
          
          <div class="mb-3">
            <p class="text-sm font-semibold mb-1" style="color: var(--text-primary)">🔐 {{ t('financeiro.configuracoes.mtlsTitle') }}</p>
            <ul class="text-xs space-y-1 ml-4" style="color: var(--text-secondary)">
              <li>• {{ t('financeiro.configuracoes.mtlsClientId') }}</li>
              <li>• {{ t('financeiro.configuracoes.mtlsCerts') }}</li>
              <li>• {{ t('financeiro.configuracoes.mtlsNoSecret') }}</li>
            </ul>
          </div>
          
          <div>
            <p class="text-sm font-semibold mb-1" style="color: var(--text-primary)">🔑 {{ t('financeiro.configuracoes.oauthTitle') }}</p>
            <ul class="text-xs space-y-1 ml-4" style="color: var(--text-secondary)">
              <li>• {{ t('financeiro.configuracoes.oauthCredentials') }}</li>
              <li>• {{ t('financeiro.configuracoes.oauthCertsOptional') }}</li>
            </ul>
          </div>
        </div>

        <!-- Botões -->
        <div class="flex gap-3 pt-4">
          <button type="submit" class="btn-primary flex-1">
            💾 {{ t('financeiro.configuracoes.saveSettings') }}
          </button>
          <button type="button" @click="testarConexao" class="btn-secondary">
            🔍 {{ t('financeiro.configuracoes.testConnection') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Card de Sincronização -->
    <div class="card">
      <h3 class="text-xl font-semibold mb-4" style="color: var(--text-primary)">🔄 {{ t('financeiro.configuracoes.boletoSync') }}</h3>
      <p class="text-sm mb-4" style="color: var(--text-secondary)">
        {{ t('financeiro.configuracoes.boletoSyncSubtitle') }}
      </p>
      <button @click="sincronizarTodos" class="btn-primary">
        🔄 {{ t('financeiro.configuracoes.syncAllPayments') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../../services/api'
import Swal from 'sweetalert2'

const { t } = useI18n()

const statusCora = ref({ configurado: false })
const mostrarSecret = ref(false)

const certificateInput = ref(null)
const privateKeyInput = ref(null)

const form = ref({
  clientId: '',
  clientSecret: '',
  certificateContent: '',
  certificateFileName: '',
  privateKeyContent: '',
  privateKeyFileName: '',
  ambiente: 'HML'
})

onMounted(() => {
  verificarStatus()
})

async function verificarStatus() {
  try {
    const response = await api.get('/financeiro/config/cora/status')
    statusCora.value = response.data
  } catch (error) {
    console.error('Erro ao verificar status:', error)
  }
}

async function handleCertificateUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  try {
    const text = await file.text()
    form.value.certificateContent = text
    form.value.certificateFileName = file.name
  } catch (error) {
    console.error('Erro ao ler certificado:', error)
    Swal.fire('Erro', t('financeiro.configuracoes.readCertificateError'), 'error')
  }
}

async function handlePrivateKeyUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  try {
    const text = await file.text()
    form.value.privateKeyContent = text
    form.value.privateKeyFileName = file.name
  } catch (error) {
    console.error('Erro ao ler chave privada:', error)
    Swal.fire('Erro', t('financeiro.configuracoes.readPrivateKeyError'), 'error')
  }
}

function limparCertificado() {
  form.value.certificateContent = ''
  form.value.certificateFileName = ''
  if (certificateInput.value) {
    certificateInput.value.value = ''
  }
}

function limparPrivateKey() {
  form.value.privateKeyContent = ''
  form.value.privateKeyFileName = ''
  if (privateKeyInput.value) {
    privateKeyInput.value.value = ''
  }
}

async function salvarCredenciais() {
  try {
    console.log('=== ENVIANDO CREDENCIAIS CORA ===')
    console.log('Client ID:', form.value.clientId ? 'presente' : 'vazio')
    console.log('Client Secret:', form.value.clientSecret ? 'presente' : 'vazio')
    console.log('Certificate:', form.value.certificateContent ? `presente (${form.value.certificateContent.length} chars)` : 'vazio')
    console.log('Private Key:', form.value.privateKeyContent ? `presente (${form.value.privateKeyContent.length} chars)` : 'vazio')
    console.log('Ambiente:', form.value.ambiente)
    
    Swal.fire({
      title: t('common.saving'),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const payload = {
      clientId: form.value.clientId,
      clientSecret: form.value.clientSecret || null,
      certificateContent: form.value.certificateContent || null,
      privateKeyContent: form.value.privateKeyContent || null,
      ambiente: form.value.ambiente
    }
    
    console.log('Payload:', payload)
    
    await api.post('/financeiro/config/cora/credenciais', payload)

    Swal.fire(t('common.success') + '!', t('financeiro.configuracoes.credentialsSaved'), 'success')
    verificarStatus()
  } catch (error) {
    console.error('Erro ao salvar credenciais:', error)
    Swal.fire('Erro', error.response?.data?.message || t('financeiro.configuracoes.saveCredentialsError'), 'error')
  }
}

async function testarConexao() {
  if (!statusCora.value.configurado) {
    Swal.fire('Atenção', t('financeiro.configuracoes.configureBeforeTest'), 'warning')
    return
  }

  Swal.fire({
    title: t('financeiro.configuracoes.testInDevelopment'),
    text: t('financeiro.configuracoes.testInDevelopmentText'),
    icon: 'info'
  })
}

async function sincronizarTodos() {
  if (!statusCora.value.configurado) {
    Swal.fire('Atenção', t('financeiro.configuracoes.configureBeforeSync'), 'warning')
    return
  }

  const result = await Swal.fire({
    title: t('financeiro.configuracoes.syncAllTitle'),
    text: t('financeiro.configuracoes.syncAllText'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('financeiro.configuracoes.syncConfirm'),
    cancelButtonText: t('common.cancel'),
    confirmButtonColor: '#2563eb'
  })

  if (result.isConfirmed) {
    try {
      Swal.fire({
        title: t('financeiro.configuracoes.syncing'),
        html: t('financeiro.configuracoes.syncingText'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const response = await api.post('/financeiro/pagamentos/sincronizar-todos')
      
      Swal.fire({
        title: t('financeiro.configuracoes.syncComplete'),
        html: `<p>${t('financeiro.configuracoes.syncUpdated', { count: response.data.totalSincronizados })}</p>`,
        icon: 'success'
      })
    } catch (error) {
      console.error('Erro ao sincronizar:', error)
      Swal.fire('Erro', error.response?.data?.message || t('financeiro.configuracoes.syncError'), 'error')
    }
  }
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 rounded-lg font-medium transition-all bg-blue-600 text-white hover:bg-blue-700;
}

.btn-secondary {
  @apply px-4 py-2 rounded-lg font-medium transition-all;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border);
}

.btn-secondary:hover {
  background-color: var(--bg-secondary);
}

.input-field {
  @apply px-3 py-2 rounded-lg border;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--border);
}

.card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}
</style>

